import type { Metadata } from "next";
import type { AppLocale } from "@/i18n/config";
import { defaultLocale, locales } from "@/i18n/config";

export const SITE_URL =
  process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/+$/, "") ||
  "https://farbin.co";

export const SITE_NAME = "FARBIN";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/favicons/512.png`;

export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

type LocalizedPathMap = Partial<Record<AppLocale, string>>;

type BuildMetadataInput = {
  locale: AppLocale;
  title: string;
  description: string;
  paths: LocalizedPathMap;
  images?: string[];
  openGraphType?: "website" | "article";
  robots?: Metadata["robots"];
};

export function ensureLeadingSlash(path: string) {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
}

export function absoluteUrl(path: string) {
  return new URL(ensureLeadingSlash(path), SITE_URL).toString();
}

export function buildLocalePath(locale: AppLocale, pathname = "") {
  const normalized = pathname === "/" ? "" : pathname;
  return ensureLeadingSlash(`/${locale}${normalized}`);
}

export function buildLocaleAlternates(paths: LocalizedPathMap) {
  const languages = Object.fromEntries(
    locales
      .filter((locale) => Boolean(paths[locale]))
      .map((locale) => [locale, canonicalPageUrl(paths[locale]!)]) as Array<
        [string, string]
      >,
  );

  const defaultPath =
    paths[defaultLocale] ?? Object.values(paths).find(Boolean);
  if (defaultPath) {
    languages["x-default"] = canonicalPageUrl(defaultPath);
  }

  return languages;
}

export function canonicalPageUrl(path: string) {
  const url = new URL(absoluteUrl(path));
  ["ref", "__fv", "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((key) => url.searchParams.delete(key));
  url.hash = "";
  return url.toString();
}

export function buildPageMetadata({
  locale,
  title,
  description,
  paths,
  images,
  openGraphType = "website",
  robots,
}: BuildMetadataInput): Metadata {
  const canonicalPath =
    paths[locale] ??
    paths[defaultLocale] ??
    Object.values(paths).find(Boolean) ??
    "/";
  const canonicalUrl = canonicalPageUrl(canonicalPath);
  const imageUrls =
    images?.filter(Boolean).map((image) => absoluteUrl(image)) ??
    [DEFAULT_OG_IMAGE];

  return {
    title,
    description,
    robots,
    alternates: {
      canonical: canonicalUrl,
      languages: buildLocaleAlternates(paths),
    },
    openGraph: {
      type: openGraphType,
      title,
      description,
      url: canonicalUrl,
      siteName: SITE_NAME,
      locale: locale === "fa" ? "fa_IR" : "en_US",
      alternateLocale: locale === "fa" ? "en_US" : "fa_IR",
      images: imageUrls.map((url) => ({ url })),
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      images: imageUrls,
    },
  };
}

export function cleanText(value?: string | null) {
  if (!value) return "";
  return value.replace(/\s+/g, " ").trim();
}

export function stripHtml(value?: string | null) {
  if (!value) return "";
  return cleanText(value.replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " "));
}

export function toPlainText(value?: string | null): string {
  if (!value) return "";
  const source = value.trim();
  if (!source) return "";

  if (source[0] === "{") {
    try {
      const parsed = JSON.parse(source);
      if (parsed?.type === "doc") {
        const parts: string[] = [];

        const walk = (node: any) => {
          if (!node) return;
          if (Array.isArray(node)) {
            node.forEach(walk);
            return;
          }
          if (node.type === "text" && typeof node.text === "string") {
            parts.push(node.text);
            return;
          }
          if (node.type === "hardBreak") {
            parts.push("\n");
            return;
          }
          if (node.type === "paragraph") {
            if (node.content) walk(node.content);
            parts.push("\n");
            return;
          }
          if (node.content) walk(node.content);
        };

        walk(parsed.content);
        return cleanText(parts.join(" ").replace(/\u00a0/g, " "));
      }
    } catch {
      // Fall through to HTML/plain text handling.
    }
  }

  if (/<\/?[a-z][\s\S]*>/i.test(source)) {
    return stripHtml(source);
  }

  return cleanText(source);
}

export function truncateDescription(value?: string | null, max = 160) {
  const text = cleanText(value);
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}
